import React, { useEffect, useState, useRef, useMemo } from "react";
import { Table } from "react-bootstrap";
import { toUSD } from "../../util/currency-formatter";
import { LockInput } from "../inputs/new-input";
import { parseFloatValue } from "../../util/common";
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import Modal from '../modal/modal'
import { roundValue } from "src/util/decimal-utils";

/**
 * Calculate finalized retentions from progressClaims array (replicates server getFinalizedRetentions)
 * @param {Array} progressClaims - Array of progress claims
 * @param {string} currentClaimId - Current claim ID to exclude from calculations
 * @returns {number} Total finalized retention amount
 */
const calculateFinalizedRetentions = (progressClaims = [], currentClaimId = '') => {
    const finalizedStatuses = ['FINALISED', 'APPROVED', 'Finalised', 'Approved']

    const totalFinalizedRetention = progressClaims
        .filter(claim => claim.u_id !== currentClaimId) // Exclude current draft
        .filter(claim => finalizedStatuses.includes(claim.progressClaimStatus || claim.status))
        .reduce((total, claim) => {
            const withheld = roundValue(parseFloat(claim.retentionAmount || 0), 2)
            const claimedBack = roundValue(parseFloat(claim.retentionClaimAmount || 0), 2)
            const netRetention = withheld - claimedBack

            return total + netRetention
            // return total + roundValue(parseFloat(claim.retentionAmount || 0))
        }, 0)

    return totalFinalizedRetention
}

/**
 * Calculate existing retentions from progressClaims array (replicates server getExistingRetentions)
 * @param {Array} progressClaims - Array of progress claims
 * @param {string} currentClaimId - Current claim ID to exclude from calculations
 * @returns {number} Total existing retention amount
 */
const calculateExistingRetentions = (progressClaims = [], currentClaimId = '') => {
    const totalRetention = progressClaims
        // .filter(claim => claim.u_id !== currentClaimId) // Exclude current draft
        .reduce((total, claim) => {
            const withheld = roundValue(parseFloat(claim.retentionAmount || 0), 2)
            const claimedBack = roundValue(parseFloat(claim.retentionClaimAmount || 0), 2)
            const netRetention = withheld - claimedBack
            return total + netRetention
            // return total + roundValue(parseFloat(claim.retentionAmount || 0))
        }, 0)

    return totalRetention
}

/**
 * Calculate excess retention due to contract value reduction
 * @param {Array} progressClaims - Array of progress claims with contract prices
 * @param {number} currentProjectTotal - Current revised project total
 * @param {number} maxRetentionPercentage - Project's max retention percentage (default: 5)
 * @returns {Object} Excess retention calculation results
 */
const calculateExcessRetention = (progressClaims = [], currentProjectTotal = 0, maxRetentionPercentage = 5) => {
    // Only consider finalized/approved claims
    const finalizedClaims = progressClaims.filter(claim =>
        ['FINALISED', 'APPROVED', 'Finalised', 'Approved'].includes(claim.progressClaimStatus)
    );

    // Calculate total retention held from finalized claims
    const totalRetentionHeld = finalizedClaims.reduce((sum, claim) => {
        const withheld = roundValue(parseFloat(claim.retentionAmount || 0), 2);
        const claimedBack = roundValue(parseFloat(claim.retentionClaimAmount || 0), 2);
        return sum + withheld - claimedBack;
    }, 0);

    // Current cap based on project's maxRetentionPercentage
    const currentCap = currentProjectTotal * (maxRetentionPercentage / 100);


    // Calculate excess
    const excessRetention = Math.max(0, totalRetentionHeld - currentCap);


    return {
        excessRetention,
        totalRetentionHeld,
        currentCap,
        hasExcess: excessRetention > 0,
        reason: excessRetention > 0 ? 'Contract value reduced - excess retention available' : null
    };
};

/**
 * Complete client-side retention calculation (replicates server calculateRetention exactly)
 * @param {Object} params - Calculation parameters
 * @param {boolean} params.autoCalculateRetention - Whether auto-calculation is enabled (default: true)
 * @param {number} params.maxRetentionPercentage - Project's max retention percentage (default: 5)
 * @param {number} params.defaultRetentionPercentage - Project's default retention per claim (default: 10)
 * @returns {Object} Complete retention calculations matching server response
 */
const calculateRetentionClientSide = ({
    claimAmount = 0,
    progressClaims = [],
    currentClaimId = '',
    projectTotal = 0,
    autoCalculateRetention = true,
    maxRetentionPercentage = 5,
    defaultRetentionPercentage = 10
}) => {
    // Step 1: Project total is already provided (calculated in preview-budget.jsx)

    // Step 2: Calculate Maximum Cap based on project's maxRetentionPercentage
    const maxCap = projectTotal * (maxRetentionPercentage / 100)

    // Step 3: Calculate Sum of Existing Retentions
    const existingRetentions = calculateExistingRetentions(progressClaims, currentClaimId)
    // Step 3a: Calculate Sum of Finalized Retentions (for helper text)
    const finalizedRetentions = calculateFinalizedRetentions(progressClaims, currentClaimId)
    // Step 4: Calculate Available Retention Capacity (can be negative if excess exists)
    const availableCapacity = maxCap - existingRetentions

    // Step 5: Calculate Standard Retention based on project's defaultRetentionPercentage
    // If autoCalculateRetention is false, standard retention is 0
    const standardRetention = autoCalculateRetention
        ? claimAmount * (defaultRetentionPercentage / 100)
        : 0

    // Step 6: Determine Actual Retention to Apply (server default withheld amount)
    // Use max(0, availableCapacity) to prevent negative deductions
    const actualRetention = Math.min(standardRetention, Math.max(0, availableCapacity))

    // Ensure retention is not negative
    const finalRetention = Math.max(0, actualRetention)

    // Step 7: Detect Excess Retention Available for Manual Claim
    // If availableCapacity is negative, this represents excess retention that can be claimed back
    const excessRetentionAmount = availableCapacity < 0 ? Math.abs(availableCapacity) : 0

    // REFACTORED POLICY: Add Retention is now manual entry only, defaulting to 0
    // Less Retention remains automated with capacity limits (unless auto-calc is disabled)
    const recommendedLess = Math.max(0, finalRetention)
    const recommendedAdd = 0  // Always default to 0 - user must manually enter

    // Return exact same structure as server calculateRetention function
    const result = {
        calculatedRetention: finalRetention,
        maxCap: maxCap,
        availableCapacity: availableCapacity,
        projectTotal: projectTotal,
        existingRetentions: existingRetentions,
        finalizedRetentions: finalizedRetentions,  // Only finalized/approved claims
        recommendedLess,
        recommendedAdd,
        excessRetentionAmount: excessRetentionAmount,
        totalRetentionHeld: existingRetentions,  // For validation purposes
        autoCalculateRetention,  // Pass through for UI display
        maxRetentionPercentage,  // Pass through for UI display
        defaultRetentionPercentage,  // Pass through for UI display
        context: {
            standardRetention: standardRetention,
            appliedPercentage: finalRetention > 0 ? (finalRetention / claimAmount) * 100 : 0,
            isCapped: finalRetention < standardRetention,
            isZeroRetention: finalRetention === 0,
            hasExcessRetention: excessRetentionAmount > 0
        }
    }

    return result
}

/*
 * PROGRESSCLAIM-RETENTION COMPONENT - Progress Claim Retention Management
 * 
 * SUMMARY:
 * Progress claim retention component that implements Australian construction industry
 * retention practices with configurable retention percentages per project.
 */

const ProgressClaimRetention = ({
    headerName = "Retentions",
    projectQuoteType = "",
    progressClaims = [],
    progressClaimId = '',
    projectTotal = 0, // Total project value for cap calculation
    autoCalculateRetention = true, // Whether automatic retention calculation is enabled
    maxRetentionPercentage = 5, // Project's maximum retention cap percentage
    defaultRetentionPercentage = 10, // Project's default retention per claim percentage
    // Optional: render current claim block at the top
    currentMeta = null, // { projectId, claimAmount, claimNumber, invoiceDate, invoiceNumber, previouslyClaimed, savedLess, savedAdd, savedClaimAmount, savedIsManualOverride }
    onCurrentChange = () => { }
}) => {
    const initializedFromSavedRef = useRef(false)
    const formatRetentionInput = (value) => roundValue(value || 0, 2).toFixed(2)

    // Defaults and override state for the current claim block
    const [context, setContext] = useState(null)
    const [less, setLess] = useState(0)
    const [add, setAdd] = useState(0)
    const [lessInput, setLessInput] = useState('0.00')
    const [addInput, setAddInput] = useState('0.00')
    const [lockLess, setLockLess] = useState(true)
    const [lockAdd, setLockAdd] = useState(false)  // Add Retention starts unlocked (manual entry)
    const [errors, setErrors] = useState({ less: '', add: '' })
    const [showLessUnlockDialog, setShowLessUnlockDialog] = useState(false)
    const [showAddUnlockDialog, setShowAddUnlockDialog] = useState(false)

    // Track if manual override is active (from saved data or current session)
    const [isManualOverride, setIsManualOverride] = useState(false)

    const retentionCalculation = useMemo(() => {
        if (!currentMeta?.projectId || !Number.isFinite(projectTotal)) return null

        const safeClaimAmount = Number.isFinite(currentMeta?.claimAmount) ? currentMeta.claimAmount : 0

        const standardCalculation = calculateRetentionClientSide({
            claimAmount: safeClaimAmount,
            progressClaims,
            currentClaimId: progressClaimId,
            projectTotal,
            autoCalculateRetention,
            maxRetentionPercentage,
            defaultRetentionPercentage
        })

        const excessCalculation = calculateExcessRetention(progressClaims, projectTotal, maxRetentionPercentage)

        return {
            ...standardCalculation,
            excessRetention: excessCalculation.excessRetention,
            hasExcess: excessCalculation.hasExcess,
            excessReason: excessCalculation.reason
        }
    }, [currentMeta?.projectId, currentMeta?.claimAmount, progressClaims, progressClaimId, projectTotal, autoCalculateRetention, maxRetentionPercentage, defaultRetentionPercentage])

    // Initialize state when calculation changes
    useEffect(() => {
        if (!retentionCalculation) return

        const ctx = retentionCalculation
        setContext(ctx)

        // REFACTORED: Add Retention is now manual entry only
        const standard = roundValue(ctx?.context?.standardRetention ?? (ctx.claimAmount * 0.1), 2)
        const availableRaw = roundValue(Math.max(ctx?.availableCapacity ?? 0, 0), 2)
        const savedLess = Number.isFinite(currentMeta?.savedLess) ? roundValue(currentMeta.savedLess, 2) : 0
        const savedAdd = Number.isFinite(currentMeta?.savedAdd) ? roundValue(currentMeta.savedAdd, 2) : 0
        const savedIsManualOverride = currentMeta?.savedIsManualOverride === true

        // Effective capacity for defaults in edit mode excludes this draft's saved values
        const availableEffective = roundValue(availableRaw + savedLess - savedAdd, 2)
        const lessDefault = roundValue(Math.min(standard, availableEffective), 2)

        // Only auto-populate Add Retention with excess when auto-calc is enabled
        const addDefault = (autoCalculateRetention && ctx?.hasExcess) ? roundValue(ctx.excessRetention, 2) : 0

        // Check if this is first load and we have saved values
        const hasSaved = Number.isFinite(currentMeta?.savedLess) || Number.isFinite(currentMeta?.savedAdd)
        const isFirstLoad = hasSaved && !initializedFromSavedRef.current

        // If manual override is active (saved or current session), preserve values - don't recalculate
        if (isFirstLoad) {
            // First load - use saved values and restore state
            const initialLess = roundValue(currentMeta?.savedLess ?? 0, 2)
            const initialAdd = roundValue(currentMeta?.savedAdd ?? 0, 2)

            setLess(initialLess)
            setAdd(initialAdd)
            setLessInput(formatRetentionInput(initialLess))
            setAddInput(formatRetentionInput(initialAdd))
            setErrors({ less: '', add: '' })
            setLockLess(true)  // If manually overridden, start unlocked
            setLockAdd(false)  // Add always starts unlocked
            setIsManualOverride(savedIsManualOverride)

            onCurrentChange({
                less: initialLess,
                add: initialAdd,
                isManualOverride: savedIsManualOverride,
                context: ctx,
                isValid: true
            })
            initializedFromSavedRef.current = true
        } else if (isManualOverride) {
            // Subsequent changes with manual override - preserve current values
            // Only update context, don't reset the values
            onCurrentChange({
                less,
                add,
                isManualOverride: true,
                context: ctx,
                isValid: !errors.less && !errors.add
            })
        } else {
            // No manual override - recalculate with defaults
            setLess(lessDefault)
            setAdd(addDefault)
            setLessInput(formatRetentionInput(lessDefault))
            setAddInput(formatRetentionInput(addDefault))
            setErrors({ less: '', add: '' })
            setLockLess(true)
            setLockAdd(false)

            onCurrentChange({
                less: lessDefault,
                add: addDefault,
                isManualOverride: false,
                context: ctx,
                isValid: true
            })
        }
    }, [retentionCalculation, currentMeta?.savedLess, currentMeta?.savedAdd, currentMeta?.savedIsManualOverride])

    // Use the client-side calculation - always up to date!
    const standard = roundValue(context?.context?.standardRetention || ((currentMeta?.claimAmount || 0) * 0.1) || 0, 2)
    const maxCap = roundValue(context?.maxCap || 0, 2)
    const existing = roundValue(context?.finalizedRetentions || 0, 2)
    const available = roundValue(context?.availableCapacity || 0, 2)

    const validate = (nextLess, nextAdd) => {
        const nextErrors = { less: '', add: '' }

        // Basic validation: no negative values
        if (nextLess < 0) nextErrors.less = '≥ 0'
        if (nextAdd < 0) nextErrors.add = '≥ 0'

        // Sanity check: Less Retention cannot exceed claim amount (always enforced)
        const claimAmount = currentMeta?.claimAmount || 0
        if (nextLess > claimAmount + 1e-6) {
            nextErrors.less = `Cannot exceed claim amount (${toUSD(claimAmount)})`
        }

        // Sanity check: Less Retention cannot exceed total project value (always enforced)
        const projectTotal = context?.projectTotal || 0
        if (nextLess > projectTotal + 1e-6) {
            nextErrors.less = `Cannot exceed project total (${toUSD(projectTotal)})`
        }

        // Add Retention validation (manual field)
        // Primary validation: Cannot claim more than finalized retention (only from approved/finalized claims)
        const finalizedRetentionHeld = context?.finalizedRetentions || 0
        if (nextAdd > finalizedRetentionHeld + 1e-6) {
            nextErrors.add = `≤ finalized retention (${toUSD(finalizedRetentionHeld)})`
        }

        // Only enforce capacity limits when auto-calculation is enabled
        if (autoCalculateRetention) {
            // Secondary validation: Cumulative retention cannot go negative
            const newCumulative = existing + nextLess - nextAdd
            if (newCumulative < -1e-6) nextErrors.add = 'Claim amount greater than available to claim'

            // Capacity validation for Less field - allow reduction even when over cap
            if (newCumulative > maxCap + 1e-6 && nextLess > (context?.existingRetentions || 0) + 1e-6) {
                nextErrors.less = `Cannot increase beyond ${maxRetentionPercentage}% cap`
            }
        }

        setErrors(nextErrors)
        return !nextErrors.less && !nextErrors.add
    }

    const handleUnlock = (field) => {
        // If currently locked, show confirmation modal to unlock
        if (field === 'less') {
            // Allow unlocking even when over cap - user may need to reduce retention
            if (lockLess) {
                setShowLessUnlockDialog(true)
            } else {
                // If currently unlocked, clicking the icon locks it back immediately
                // Keep the current value - locking doesn't clear manual override
                setLockLess(true)
                onCurrentChange({ less, add, isManualOverride: isManualOverride, context, isValid: !errors.less && !errors.add })
            }
        }
        if (field === 'add') {
            if (lockAdd) {
                setShowAddUnlockDialog(true)
            } else {
                setLockAdd(true)
                onCurrentChange({ less, add, isManualOverride: isManualOverride, context, isValid: !errors.less && !errors.add })
            }
        }
    }

    const confirmUnlock = (field) => {
        if (field === 'less') {
            setLockLess(false)
            setShowLessUnlockDialog(false)
            // Don't set isManualOverride here - wait for actual edit in handleChange
        }
        if (field === 'add') {
            setLockAdd(false)
            setShowAddUnlockDialog(false)
        }
    }

    const cancelUnlock = (field) => {
        if (field === 'less') setShowLessUnlockDialog(false)
        if (field === 'add') setShowAddUnlockDialog(false)
    }

    const handleChange = (field, value) => {
        const amount = roundValue(parseFloatValue(value || 0), 2)
        if (field === 'less') {
            let clampedLess

            if (!autoCalculateRetention) {
                // Auto-calc disabled - user has complete discretion, no clamping
                clampedLess = amount
            } else {
                // Auto-calc enabled - apply standard clamping rules
                const savedLess = Number.isFinite(currentMeta?.savedLess) ? roundValue(currentMeta.savedLess, 2) : 0
                const savedAdd = Number.isFinite(currentMeta?.savedAdd) ? roundValue(currentMeta.savedAdd, 2) : 0
                const availableBefore = roundValue(Math.max((context?.availableCapacity || 0) + savedLess - savedAdd, 0), 2)

                if (available <= 0) {
                    // Over cap - allow reduction only
                    clampedLess = Math.min(amount, savedLess)
                } else {
                    // Normal clamping to standard retention or available capacity
                    clampedLess = Math.min(amount, Math.min(standard, availableBefore))
                }
            }

            // User editing while unlocked = manual override
            const newIsManualOverride = !lockLess ? true : isManualOverride

            const ok = validate(clampedLess, add)
            setLess(clampedLess)
            setLessInput(Math.abs(clampedLess - amount) > 1e-6 ? formatRetentionInput(clampedLess) : value)
            if (!lockLess) setIsManualOverride(true)
            onCurrentChange({ less: clampedLess, add, isManualOverride: newIsManualOverride, context, isValid: ok })
        } else {
            // Add Retention handling - manual entry with validation
            const ok = validate(less, amount)
            setAdd(amount)
            setAddInput(value)
            onCurrentChange({ less, add: amount, isManualOverride: isManualOverride, context, isValid: ok })
        }
    }

    const lessRetentions = Array.isArray(progressClaims) ? progressClaims.reduce((acc, claim) => {
        if (claim.u_id === progressClaimId) return acc;
        acc.push({
            progressClaimId: claim.id,
            progressClaimUniqueId: claim.u_id,
            total: Number(claim.totalValue) || 0,
            claimAmount: Number(claim.claimAmount) || 0,
            retentionAmount: Number(claim.retentionAmount) || 0
        });
        return acc;
    }, []) : [];

    const net = Math.max(0, (currentMeta?.claimAmount || 0) - (less || 0) + (add || 0))

    // Derive previously retained total from provided progress claims (mirrors invoices-list.jsx summary logic)
    const previouslyRetainedTotal = Array.isArray(progressClaims)
        ? progressClaims.reduce((sum, row) => {
            if (row.u_id === progressClaimId) return sum
            return sum + (Number(row.retentionAmount) || 0)
        }, 0)
        : 0

    // Available capacity from backend already accounts for all existing retentions (including this draft's saved amounts)
    const baseAvailable = roundValue(context?.availableCapacity ?? Math.max((maxCap || 0) - previouslyRetainedTotal, 0), 2)
    // If editing a draft with saved values, adjust only by the delta from saved → current ui values
    const savedLess = Number.isFinite(currentMeta?.savedLess) ? roundValue(currentMeta.savedLess, 2) : 0
    // const savedAdd = Number.isFinite(currentMeta?.savedAdd) ? roundValue(currentMeta.savedAdd) : 0
    const deltaLess = roundValue(savedLess - (less || 0), 2)
    const availableAfterThisClaim = roundValue(Math.max(baseAvailable + deltaLess, 0), 2)

    return (
        <div className="row mt-2 mb-4">
            <div className="col-12">
                <div className="material-list-block mt-3">
                    <h2 className="content-heading text-uppercase mb-0" data-cy="retention-section-heading">{headerName}</h2>
                    <div className="table-responsive">
                        <Table bordered className="table-create table-material table-material-ch">
                            <thead>
                                <tr>
                                    <th className="w-auto">{currentMeta ? 'Claim Number' : 'Progress Claim Id'}</th>
                                    {currentMeta && <th>Date</th>}
                                    {currentMeta && <th>Invoice Number</th>}
                                    <th className="text-right">Total Amount Claimed</th>
                                    <th className="text-right">{currentMeta ? 'Less Previously Claimed' : 'Progress Claim Amount'}</th>
                                    {currentMeta ? <th className="text-right">This Claim Amount</th> : (projectQuoteType === "quote" && <th className="text-right">Retention Amount</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {currentMeta && (
                                    <>
                                        <tr>
                                            <td>{currentMeta.claimNumber || 'Not Generated'}</td>
                                            <td>{currentMeta.invoiceDate || ''}</td>
                                            <td>{currentMeta.invoiceNumber || 'Not Invoiced'}</td>
                                            <td className="text-right">{toUSD(currentMeta.claimAmount || 0)}</td>
                                            <td className="text-right">{toUSD(0)}</td>
                                            <td className="text-right">{toUSD(currentMeta.claimAmount || 0)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan={5} data-cy="less-retention-label"><strong>Less Retention (This Claim)</strong></td>
                                            <td className="text-right">
                                                <div className="d-flex align-items-center gap-2 justify-content-end">
                                                    <LockInput
                                                        name="lessRetention"
                                                        type="text"
                                                        label={''}
                                                        value={lessInput}
                                                        onChange={(e) => handleChange('less', e.target.value)}
                                                        touched={true}
                                                        valid={!errors.less}
                                                        error={errors.less}
                                                        disabled={lockLess}
                                                        formatDecimals={true}
                                                        decimalPlaces={2}
                                                        selectOnFocus={true}
                                                        className="input-block-sm retention-lock"
                                                        data-cy="less-retention-input"
                                                        lock={<span className="lock-icon">
                                                            {lockLess ? (
                                                                <LockIcon
                                                                    data-cy="less-retention-lock-locked"
                                                                    onClick={() => handleUnlock('less')}
                                                                    title={
                                                                        available <= 0
                                                                            ? `Over ${maxRetentionPercentage}% retention cap - unlock to reduce`
                                                                            : isManualOverride
                                                                                ? "Manually set value - click to unlock and edit"
                                                                                : autoCalculateRetention
                                                                                    ? "Auto-calculated - click to unlock for manual entry"
                                                                                    : "Manual entry (auto-calculation disabled for this project)"
                                                                    }
                                                                />
                                                            ) : (
                                                                <LockOpenIcon
                                                                    data-cy="less-retention-lock-unlocked"
                                                                    onClick={() => handleUnlock('less')}
                                                                    title="Click to lock (value will persist on claim changes)"
                                                                />
                                                            )}
                                                        </span>}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={5} data-cy="add-retention-label"><strong>Add Retention (This Claim)</strong></td>
                                            <td className="text-right">
                                                <div className="d-flex align-items-center gap-2 justify-content-end">
                                                    <LockInput
                                                        name="addRetention"
                                                        type="text"
                                                        label={''}
                                                        value={addInput}
                                                        onChange={(e) => handleChange('add', e.target.value)}
                                                        touched={true}
                                                        valid={!errors.add}
                                                        error={errors.add}
                                                        disabled={lockAdd}
                                                        formatDecimals={true}
                                                        decimalPlaces={2}
                                                        selectOnFocus={true}
                                                        className="input-block-sm retention-lock"
                                                        data-cy="add-retention-input"
                                                        lock={<span className="lock-icon">
                                                            {lockAdd ? (
                                                                <LockIcon
                                                                    data-cy="add-retention-lock-locked"
                                                                    onClick={() => handleUnlock('add')}
                                                                />
                                                            ) : (
                                                                <LockOpenIcon
                                                                    data-cy="add-retention-lock-unlocked"
                                                                    onClick={() => handleUnlock('add')}
                                                                />
                                                            )}
                                                        </span>}
                                                    />
                                                </div>
                                                <div className="small text-muted mt-1" data-cy="available-to-claim">
                                                    Available to claim: {toUSD(context?.finalizedRetentions || 0)}
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan={5} data-cy="total-claim-label"><strong>Total Claim (After Retention)</strong></td>
                                            <td className="text-right"><strong>{toUSD(net)} Exc. GST</strong></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={6} className="small text-muted" data-cy="retention-calculation-summary">
                                                {autoCalculateRetention ? (
                                                    <>
                                                        Standard ({defaultRetentionPercentage}%): {toUSD(standard || 0)} | Max cap ({maxRetentionPercentage}%): {toUSD(maxCap || 0)} | Available capacity: {toUSD(availableAfterThisClaim || 0)}
                                                        {available <= 0 && <span> | Cap reached — no retention applied by default.</span>}
                                                    </>
                                                ) : (
                                                    <span className="text-warning">Auto-calculation disabled - user has complete discretion (no automatic limits apply)</span>
                                                )}
                                                {isManualOverride && <span className="text-info"> | <strong>Manual Override Active</strong> - value will persist on claim changes</span>}
                                            </td>
                                        </tr>
                                    </>
                                )}

                                {!currentMeta && lessRetentions.length === 0 && (
                                    <tr>
                                        <td className="text-center" colSpan={projectQuoteType === "quote" ? 4 : 3}>
                                            No Data to Show
                                        </td>
                                    </tr>
                                )}

                                {!currentMeta && lessRetentions.length > 0 && (
                                    lessRetentions.map((retention, index) => (
                                        <tr key={index}>
                                            <td>{retention.progressClaimId || ""}</td>
                                            <td className="text-right">{toUSD(retention.total)}</td>
                                            <td className="text-right">{toUSD(retention.claimAmount)}</td>
                                            {projectQuoteType === "quote" && (<td className="text-right">{toUSD(retention.retentionAmount)}</td>)}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                    {/* Unlock confirmations using shared Modal for consistent style */}
                    <Modal
                        open={showLessUnlockDialog}
                        onClose={() => cancelUnlock('less')}
                        modalHeading={'Unlock Less Retention'}
                        showActionBtn={true}
                        modalSaveTxt={'Unlock'}
                        modalCloseTxt={'Cancel'}
                        onClick={() => confirmUnlock('less')}
                        primary
                    >
                        <div>
                            Are you sure you want to override the calculated less retention for this claim?
                            {available <= 0 && (
                                <div className="mt-2 text-warning">
                                    <strong>Note:</strong> This claim is currently over the {maxRetentionPercentage}% retention cap.
                                    You can reduce the retention amount to get back under the cap.
                                </div>
                            )}
                            {!autoCalculateRetention && (
                                <div className="mt-2 text-info">
                                    <strong>Info:</strong> Auto-calculation is disabled for this project.
                                    You can manually enter any retention amount.
                                </div>
                            )}
                        </div>
                    </Modal>
                    <Modal
                        open={showAddUnlockDialog}
                        onClose={() => cancelUnlock('add')}
                        modalHeading={'Unlock Add Retention'}
                        showActionBtn={true}
                        modalSaveTxt={'Unlock'}
                        modalCloseTxt={'Cancel'}
                        onClick={() => confirmUnlock('add')}
                        primary
                    >
                        <div>
                            Are you sure you want to override the calculated add retention for this claim?
                        </div>
                    </Modal>
                </div>
            </div>
        </div>
    );
}

export default ProgressClaimRetention;
